import React, { useState } from 'react';
import { useMarketingConditionPreviewQuery } from '../lib/Queries';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';

const ConditionMatchesPreview = ({ automationId, schema, action, conditions, className, flowableType = 'App\\Models\\Client' }) => {
    const [showModal, setShowModal] = useState(false);

    // Only query if we have valid conditions
    const hasConditions = conditions?.conditions?.length > 0;

    const { data, isLoading, error } = useMarketingConditionPreviewQuery(
        flowableType,
        conditions,
        action,
        hasConditions
    );

    const total = data?.total || 0;
    const preview = data?.preview || [];
    const unsubscribedCount = data?.unsubscribed_count || 0;
    const notOptedInCount = data?.not_opted_in_count || 0;

    // Don't show anything if there are no conditions
    if (!hasConditions) {
        return null;
    }

    // Get the entity type name for display
    const getEntityTypeName = (type) => {
        switch (type) {
            case 'App\\Models\\Client':
                return 'clients';
            case 'App\\Models\\BookingCalendarEvent':
                return 'booking events';
            case 'App\\Models\\ClassInstance':
                return 'classes';
            default:
                return 'records';
        }
    };

    const entityTypeName = getEntityTypeName(flowableType);

    const handleClick = () => {
        if (total > 0) {
            setShowModal(true);
        }
    };

    const renderTableHeaders = () => {
        switch (flowableType) {
            case 'App\\Models\\Client':
                return (
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            First Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                        </th>
                    </tr>
                );
            case 'App\\Models\\BookingCalendarEvent':
                return (
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Start Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Event Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Client Name
                        </th>
                    </tr>
                );
            case 'App\\Models\\ClassInstance':
                return (
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Start Time
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Event Type
                        </th>
                    </tr>
                );
            default:
                return (
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                        </th>
                    </tr>
                );
        }
    };

    const renderTableRow = (item, index) => {
        switch (flowableType) {
            case 'App\\Models\\Client':
                return (
                    <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.first_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.last_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.email || 'N/A'}
                        </td>
                    </tr>
                );
            case 'App\\Models\\BookingCalendarEvent':
                return (
                    <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.start_time || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.event_type || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.client_name || 'N/A'}
                        </td>
                    </tr>
                );
            case 'App\\Models\\ClassInstance':
                return (
                    <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.start_time || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.event_type || 'N/A'}
                        </td>
                    </tr>
                );
            default:
                return (
                    <tr key={item.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {item.name || 'N/A'}
                        </td>
                    </tr>
                );
        }
    };

    if (isLoading) {
        return (
            <div
                className={`flex items-center space-x-2 text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-md ${className}`}
            >
                <UserGroupIcon className="w-4 h-4" />
                <span>Checking matches...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className={
                    `flex items-center space-x-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md ${className}`
                }
            >
                <UserGroupIcon className="w-4 h-4" />
                <span>Error checking matches</span>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-2">
                <div
                    className={`flex items-start w-fit space-x-2 text-sm px-3 py-2 rounded-md text-yellow-700 bg-yellow-50 ${className} ${
                        total > 0
                            ? 'cursor-pointer '
                            : ''
                    }`}
                    onClick={handleClick}
                >
                    <UserGroupIcon className="w-4 h-4 mt-0.5" />
                    <div>
                        <span>
                        {total === 0
                            ? `No ${entityTypeName} match this condition`
                            : `${total} ${entityTypeName} match this condition`
                        }
                    </span>
                        {unsubscribedCount > 0 && (
                            <div className="">{unsubscribedCount} unsubscribed (will not receive emails)</div>
                        )}
                        {notOptedInCount > 0 && (
                            <div className="">
                                <span>{notOptedInCount} not opted in to marketing (will not receive emails)</span>
                            </div>
                        )}
                    </div>
                    </div>
            </div>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={`Matching ${entityTypeName.charAt(0).toUpperCase() + entityTypeName.slice(1)}`}
                size="lg"
            >
                <div className="p-2">
                    <div className="mb-4 space-y-2">
                        <p className="text-sm text-gray-600">
                            Showing first {Math.min(20, total)} of {total} matching {entityTypeName}
                        </p>

                        {/* Show email warnings in modal for email actions on clients */}
                        {action === 'email' && flowableType === 'App\\Models\\Client' && (
                            <div className="space-y-2">
                                {unsubscribedCount > 0 && (
                                    <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md">
                                        ⚠️ {unsubscribedCount} clients are unsubscribed and will not receive emails
                                    </div>
                                )}
                                {notOptedInCount > 0 && (
                                    <div className="text-sm text-orange-600 bg-orange-50 px-3 py-2 rounded-md">
                                        ⚠️ {notOptedInCount} clients have not opted in to marketing and will not receive emails
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                        <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                                {renderTableHeaders()}
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {preview.map((item, index) => renderTableRow(item, index))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="button"
                            className="btn-light-pill"
                            onClick={() => setShowModal(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ConditionMatchesPreview;
